import { type ApplicationSettingTheme, type DesignThemeType, type FieldInputConfigProtocol } from '@lighthouse/core'

import type { DesignAndInteractionStyle, ThemeDesignInitConfig } from './common'

type ThemeFieldInputDesignInitConfig = Record<DesignThemeType, ThemeDesignInitConfig | undefined>

// 主题初始化样式设计配置
const getBaseThemeFieldDesignConfig = (themeColorId?: string): ThemeFieldInputDesignInitConfig => ({
    LIGHT: {
        design: {
            theme: 'LIGHT',
            labelColor: { type: 'color', color: '#101828' },
            color: { type: 'color', color: '#101828' },
            iconColor: { type: 'color', color: '#98A2B3' },
            placeholderColor: { type: 'color', color: '#98A2B3' },
            radius: [6, 6, 6, 6],
            background: { type: 'color', color: '#fff' },
            border: { mode: 'each', each: [1, 1, 1, 1], type: 'color', color: '#E4E7EC' }
        },
        hoverStyle: {
            open: true,
            design: {
                labelColor: { type: 'color', color: '#101828' },
                color: { type: 'color', color: '#101828' },
                iconColor: { type: 'color', color: '#98A2B3' },
                placeholderColor: { type: 'color', color: '#98A2B3' },
                radius: [6, 6, 6, 6],
                background: { type: 'color', color: '#fff' },
                border: { mode: 'each', each: [1, 1, 1, 1], type: 'color', color: '#E4E7EC' }
            }
        },
        activeStyle: {
            open: true,
            design: {
                labelColor: { type: 'color', color: '#101828' },
                color: { type: 'color', color: '#101828' },
                iconColor: { type: 'color', color: '#98A2B3' },
                checkedColor: { type: 'color', color: themeColorId },
                placeholderColor: { type: 'color', color: '#98A2B3' },
                radius: [6, 6, 6, 6],
                background: { type: 'color', color: '#fff' },
                border: { mode: 'each', each: [1, 1, 1, 1], type: 'color', color: themeColorId }
            }
        },
        disabledStyle: {
            open: true,
            design: {
                labelColor: { type: 'color', color: '#101828' },
                color: { type: 'color', color: '#101828' },
                iconColor: { type: 'color', color: '#98A2B3' },
                placeholderColor: { type: 'color', color: '#98A2B3' },
                radius: [6, 6, 6, 6],
                background: { type: 'color', color: '#ECEFF4' },
                border: { mode: 'each', each: [1, 1, 1, 1], type: 'color', color: '#E4E7EC' }
            }
        }
    },
    DARK: {
        design: {
            theme: 'DARK',
            labelColor: { type: 'color', color: '#ffffff' },
            color: { type: 'color', color: '#ffffff' },
            iconColor: { type: 'color', color: '#858688' },
            placeholderColor: { type: 'color', color: '#535353' },
            radius: [6, 6, 6, 6],
            background: { type: 'color', color: '#292727b3' },
            border: { mode: 'each', each: [1, 1, 1, 1], type: 'color', color: '#444548' }
        },
        hoverStyle: {
            open: true,
            design: {
                color: { type: 'color', color: '#ffffff' },
                iconColor: { type: 'color', color: '#858688' },
                placeholderColor: { type: 'color', color: '#535353' },
                radius: [6, 6, 6, 6],
                background: { type: 'color', color: '#292727b3' },
                border: { mode: 'each', each: [1, 1, 1, 1], type: 'color', color: '#444548' }
            }
        },
        activeStyle: {
            open: true,
            design: {
                color: { type: 'color', color: '#ffffff' },
                iconColor: { type: 'color', color: '#858688' },
                placeholderColor: { type: 'color', color: '#535353' },
                checkedColor: { type: 'color', color: themeColorId },
                radius: [6, 6, 6, 6],
                background: { type: 'color', color: '#292727b3' },
                border: { mode: 'each', each: [1, 1, 1, 1], type: 'color', color: themeColorId }
            }
        },
        disabledStyle: {
            open: true,
            design: {
                color: { type: 'color', color: '#ffffff' },
                iconColor: { type: 'color', color: '#858688' },
                placeholderColor: { type: 'color', color: '#535353' },
                radius: [6, 6, 6, 6],
                background: { type: 'color', color: '#292727b3' },
                border: { mode: 'each', each: [1, 1, 1, 1], type: 'color', color: '#444548' }
            }
        }
    },
    CUSTOM: undefined
})

const getSelectDesignConfig = (
    baseThemeFieldDesignConfig: ThemeFieldInputDesignInitConfig
): Record<'LIGHT' | 'DARK', DesignAndInteractionStyle> => {
    return {
        LIGHT: {
            design: {
                ...baseThemeFieldDesignConfig.LIGHT?.design,
                radius: [100, 100, 100, 100]
            },
            hoverStyle: {
                open: !!baseThemeFieldDesignConfig.LIGHT?.hoverStyle?.open,
                design: {
                    ...baseThemeFieldDesignConfig.LIGHT?.hoverStyle?.design,
                    radius: [100, 100, 100, 100]
                }
            },
            disabledStyle: {
                open: !!baseThemeFieldDesignConfig.LIGHT?.disabledStyle?.open,
                design: {
                    ...baseThemeFieldDesignConfig.LIGHT?.disabledStyle?.design,
                    radius: [100, 100, 100, 100]
                }
            }
        },
        DARK: {
            design: {
                ...baseThemeFieldDesignConfig.DARK?.design,
                radius: [100, 100, 100, 100]
            },
            hoverStyle: {
                open: !!baseThemeFieldDesignConfig.DARK?.hoverStyle?.open,
                design: {
                    ...baseThemeFieldDesignConfig.DARK?.hoverStyle?.design,
                    radius: [100, 100, 100, 100]
                }
            },
            disabledStyle: {
                open: !!baseThemeFieldDesignConfig.DARK?.disabledStyle?.open,
                design: {
                    ...baseThemeFieldDesignConfig.DARK?.disabledStyle?.design,
                    radius: [100, 100, 100, 100]
                }
            }
        }
    }
}

type ThemeFieldDesignConfigOptions = {
    theme?: ApplicationSettingTheme
    relativeSelectMode?: string
}

export const themeFieldDesignConfig = (
    type: FieldInputConfigProtocol['inputType'],
    options?: ThemeFieldDesignConfigOptions
): ThemeFieldInputDesignInitConfig => {
    const theme = options?.theme
    const primaryPointer = theme?.primaryPointer
    const baseThemeFieldDesignConfig = getBaseThemeFieldDesignConfig(primaryPointer)
    const selectDesignConfig = getSelectDesignConfig(baseThemeFieldDesignConfig)
    switch (type) {
        case 'person': {
            return {
                LIGHT: {
                    ...baseThemeFieldDesignConfig.LIGHT,
                    personDesign: {
                        design: {
                            color: { type: 'color', color: '#101828' },
                            iconColor: { type: 'color', color: '#98A2B3' }
                        },
                        hoverStyle: {
                            open: true,
                            design: {
                                color: { type: 'color', color: '#101828' },
                                iconColor: { type: 'color', color: '#98A2B3' }
                            }
                        },
                        disabledStyle: {
                            open: true,
                            design: {
                                color: { type: 'color', color: '#101828' },
                                iconColor: { type: 'color', color: '#98A2B3' }
                            }
                        }
                    }
                },
                DARK: {
                    ...baseThemeFieldDesignConfig.DARK,
                    personDesign: {
                        design: {
                            color: { type: 'color', color: '#ffffff' },
                            iconColor: { type: 'color', color: '#858688' }
                        },
                        hoverStyle: {
                            open: true,
                            design: {
                                color: { type: 'color', color: '#ffffff' },
                                iconColor: { type: 'color', color: '#858688' }
                            }
                        },
                        disabledStyle: {
                            open: true,
                            design: {
                                color: { type: 'color', color: '#ffffff' },
                                iconColor: { type: 'color', color: '#858688' }
                            }
                        }
                    }
                },
                CUSTOM: baseThemeFieldDesignConfig.CUSTOM
            }
        }

        case 'file': {
            return {
                LIGHT: {
                    design: baseThemeFieldDesignConfig.LIGHT?.design,
                    hoverStyle: baseThemeFieldDesignConfig.LIGHT?.hoverStyle,
                    fileDesign: {
                        design: {
                            color: { type: 'color', color: '#101828' },
                            iconColor: { type: 'color', color: '#98A2B3' },
                            radius: [6, 6, 6, 6],
                            background: {
                                type: 'color',
                                color: '#ECEFF4'
                            }
                        },
                        hoverStyle: {
                            open: true,
                            design: {
                                color: { type: 'color', color: '#101828' },
                                iconColor: { type: 'color', color: '#98A2B3' },
                                radius: [6, 6, 6, 6],
                                background: {
                                    type: 'color',
                                    color: '#ECEFF4'
                                }
                            }
                        },
                        disabledStyle: {
                            open: true,
                            design: {
                                color: { type: 'color', color: '#101828' },
                                iconColor: { type: 'color', color: '#98A2B3' },
                                radius: [6, 6, 6, 6],
                                background: {
                                    type: 'color',
                                    color: '#ECEFF4'
                                }
                            }
                        }
                    }
                },
                DARK: {
                    design: baseThemeFieldDesignConfig.DARK?.design,
                    hoverStyle: baseThemeFieldDesignConfig.DARK?.hoverStyle,
                    fileDesign: {
                        design: {
                            color: { type: 'color', color: '#fffff' },
                            iconColor: { type: 'color', color: '#858688' },
                            radius: [6, 6, 6, 6],
                            background: {
                                type: 'color',
                                color: '#292727b3'
                            }
                        },
                        hoverStyle: {
                            open: true,
                            design: {
                                color: { type: 'color', color: '#fffff' },
                                iconColor: { type: 'color', color: '#858688' },
                                radius: [6, 6, 6, 6],
                                background: {
                                    type: 'color',
                                    color: '#292727b3'
                                }
                            }
                        },
                        disabledStyle: {
                            open: true,
                            design: {
                                color: { type: 'color', color: '#fffff' },
                                iconColor: { type: 'color', color: '#858688' },
                                radius: [6, 6, 6, 6],
                                background: {
                                    type: 'color',
                                    color: '#292727b3'
                                }
                            }
                        }
                    }
                },
                CUSTOM: baseThemeFieldDesignConfig.CUSTOM
            }
        }

        case 'slider': {
            return {
                LIGHT: {
                    ...baseThemeFieldDesignConfig.LIGHT,
                    sliderDesign: {
                        design: {
                            color: { type: 'color', color: primaryPointer },
                            background: { type: 'color', color: '#ECEFF4' }
                        }
                    }
                },
                DARK: {
                    ...baseThemeFieldDesignConfig.DARK,
                    sliderDesign: {
                        design: {
                            color: { type: 'color', color: primaryPointer },
                            background: baseThemeFieldDesignConfig.DARK?.design?.background
                        }
                    }
                },
                CUSTOM: baseThemeFieldDesignConfig.CUSTOM
            }
        }
        case 'relativeSelect': {
            if (options?.relativeSelectMode === 'tag') {
                return {
                    LIGHT: {
                        ...baseThemeFieldDesignConfig.LIGHT,
                        activeStyle: {
                            open: !!baseThemeFieldDesignConfig.LIGHT?.activeStyle?.open,
                            design: {
                                ...baseThemeFieldDesignConfig.LIGHT?.activeStyle?.design,
                                background: { type: 'color', color: primaryPointer },
                                color: { type: 'color', color: '#fff' }
                            }
                        },
                        selectDesign: selectDesignConfig.LIGHT
                    },
                    DARK: {
                        ...baseThemeFieldDesignConfig.DARK,
                        activeStyle: {
                            open: !!baseThemeFieldDesignConfig.LIGHT?.activeStyle?.open,
                            design: {
                                ...baseThemeFieldDesignConfig.LIGHT?.activeStyle?.design,
                                background: { type: 'color', color: primaryPointer },
                                color: { type: 'color', color: '#fff' }
                            }
                        },
                        selectDesign: selectDesignConfig.DARK
                    },
                    CUSTOM: baseThemeFieldDesignConfig.CUSTOM
                }
            }

            if (options?.relativeSelectMode === 'checkbox') {
                return {
                    LIGHT: {
                        ...baseThemeFieldDesignConfig.LIGHT,
                        activeStyle: {
                            open: baseThemeFieldDesignConfig.LIGHT?.activeStyle?.open,
                            design: {
                                ...baseThemeFieldDesignConfig.LIGHT?.activeStyle?.design,
                                background: { type: 'color', color: primaryPointer },
                                checkedColor: { type: 'color', color: '#fff' }
                            }
                        },
                        selectDesign: selectDesignConfig.LIGHT
                    },
                    DARK: {
                        ...baseThemeFieldDesignConfig.DARK,
                        activeStyle: {
                            open: baseThemeFieldDesignConfig.LIGHT?.activeStyle?.open,
                            design: {
                                ...baseThemeFieldDesignConfig.LIGHT?.activeStyle?.design,
                                color: { type: 'color', color: '#fff' },
                                background: { type: 'color', color: primaryPointer },
                                checkedColor: { type: 'color', color: '#fff' }
                            }
                        },
                        selectDesign: selectDesignConfig.DARK
                    },
                    CUSTOM: baseThemeFieldDesignConfig.CUSTOM
                }
            }
            return {
                LIGHT: {
                    ...baseThemeFieldDesignConfig.LIGHT,
                    selectDesign: selectDesignConfig.LIGHT
                },
                DARK: {
                    ...baseThemeFieldDesignConfig.DARK,
                    selectDesign: selectDesignConfig.DARK
                },
                CUSTOM: baseThemeFieldDesignConfig.CUSTOM
            }
        }

        default: {
            return {
                LIGHT: {
                    design: baseThemeFieldDesignConfig.LIGHT?.design,
                    hoverStyle: baseThemeFieldDesignConfig.LIGHT?.hoverStyle,
                    activeStyle: baseThemeFieldDesignConfig.LIGHT?.activeStyle,
                    disabledStyle: baseThemeFieldDesignConfig.LIGHT?.disabledStyle
                },
                DARK: {
                    design: baseThemeFieldDesignConfig.DARK?.design,
                    hoverStyle: baseThemeFieldDesignConfig.DARK?.hoverStyle,
                    activeStyle: baseThemeFieldDesignConfig.DARK?.activeStyle,
                    disabledStyle: baseThemeFieldDesignConfig.DARK?.disabledStyle
                },
                CUSTOM: baseThemeFieldDesignConfig.CUSTOM
            }
        }
    }
}
